import dayjs from 'dayjs';
import cs from 'dayjs/locale/cs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import type { useFormatter } from 'next-intl';

export const DATE_FORMAT = 'DD/MM/YY';
export const TIME_FORMAT = 'H:mm';
export const DATE_TIME_FORMAT = 'DD/MM/YY HH:mm';

export const setupDaysJs = () => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.extend(localizedFormat);
  dayjs.locale(cs);
  dayjs.tz.setDefault('UTC');
};

export function formatDateRange(startDate: string, endDate: string, format = DATE_FORMAT) {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (end.isSame(start, 'day')) {
    return end.format(format);
  }
  if (end.isSame(start, 'month')) {
    return `${start.format('DD')}–${end.format(format)}`;
  }
  if (!end.isSame(start, 'month') && end.isSame(start, 'year')) {
    return `${start.format('DD/MM')}–${end.format(format)}`;
  }
  if (!end.isSame(start, 'year')) {
    return `${start.format(format)}–${end.format(format)}`;
  }
  return undefined;
}

export function formatDate(date: string | Date | undefined, format = DATE_FORMAT): string {
  return dayjs(date).format(format);
}

export function getToday(format = DATE_FORMAT): string {
  return dayjs().format(format);
}

export function getDiffInDays(startDate: string, endDate: string): number {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return end.diff(start, 'day');
}

// Backend ISO strings often come without a timezone suffix but represent UTC.
// `new Date(value)` would parse these as local time, shifting timestamps by the
// client's offset. These helpers normalize such strings to UTC.
export function ensureUtcIso(value: string): string {
  return /[Zz]|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`;
}

export function parseUtcIso(value: string): Date {
  return new Date(ensureUtcIso(value));
}

// Formats ISO datetime to "DD-MMM-YYYY HH:mm:ss" with uppercased month (e.g., 15-JUL-2025 23:59:59)
export function formatFullTimestamp(iso: string): string {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return String(iso);

  const monthAbbr = [
    'JAN',
    'FEB',
    'MAR',
    'APR',
    'MAY',
    'JUN',
    'JUL',
    'AUG',
    'SEP',
    'OCT',
    'NOV',
    'DEC',
  ];
  const dd = String(date.getDate()).padStart(2, '0');
  const mon = monthAbbr[date.getMonth()];
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${dd}-${mon}-${yyyy} ${hh}:${mm}:${ss}`;
}

// Formats ISO datetime to "HH:mm:ss DD-Mon-YYYY" (e.g., 23:59:44 18-May-2026).
export function formatTimeDate(iso: string): string {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return String(iso);

  const monthAbbr = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const mon = monthAbbr[date.getMonth()];
  const yyyy = date.getFullYear();
  return `${hh}:${mm}:${ss} ${dd}-${mon}-${yyyy}`;
}

// Formats ISO datetime to "DD-MMM-YYYY/ HH:mm:ss" UTC (e.g., 04-Apr-2025/ 08:10:12).
export function formatDateSlashTime(iso: string): string {
  if (!dayjs.utc) dayjs.extend(utc);

  const date = dayjs.utc(iso);
  return date.isValid() ? date.format('DD-MMM-YYYY/ HH:mm:ss') : String(iso);
}

// Formats epoch ms to "YYYY-MM-DD" UTC (e.g., 2026-10-02).
export function formatDateIsoUtc(ms: number): string {
  if (!dayjs.utc) dayjs.extend(utc);
  return dayjs.utc(ms).format('YYYY-MM-DD');
}

// Formats epoch ms to "HH:mm:ss" UTC (e.g., 10:20:25).
export function formatTimeUtc(ms: number): string {
  if (!dayjs.utc) dayjs.extend(utc);
  return dayjs.utc(ms).format('HH:mm:ss');
}

// Formats ISO datetime for Account Activities table:
// - "Today HH:mm" if date is today (in UTC)
// - "DD-MMM-YYYY" if date is not today (in UTC)
export function formatActivityTime(iso: string): string {
  // Ensure UTC plugins are loaded
  if (!dayjs.utc) {
    dayjs.extend(utc);
  }

  const date = dayjs.utc(iso);
  if (!date.isValid()) return String(iso);

  const today = dayjs.utc().startOf('day');
  const activityDate = date.startOf('day');

  if (activityDate.isSame(today, 'day')) {
    // Today: show "Today HH:mm"
    const hh = String(date.hour()).padStart(2, '0');
    const mm = String(date.minute()).padStart(2, '0');
    return `Today ${hh}:${mm}`;
  } else {
    // Not today: show "DD-MMM-YYYY"
    const monthAbbr = [
      'JAN',
      'FEB',
      'MAR',
      'APR',
      'MAY',
      'JUN',
      'JUL',
      'AUG',
      'SEP',
      'OCT',
      'NOV',
      'DEC',
    ];
    const dd = String(date.date()).padStart(2, '0');
    const mon = monthAbbr[date.month()];
    const yyyy = date.year();
    return `${dd}-${mon}-${yyyy}`;
  }
}

/** The next 00:00 UTC strictly after `from` — when the daily draw turns over. */
export function nextUtcMidnight(from: Date): Date {
  return new Date(
    Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate() + 1, 0, 0, 0, 0),
  );
}

/** The first instant of the next calendar month, UTC — when a monthly period turns over. */
export function nextUtcMonthStart(from: Date): Date {
  return new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + 1, 1, 0, 0, 0, 0));
}

/** The first instant of the next calendar year, UTC — when a yearly period turns over. */
export function nextUtcYearStart(from: Date): Date {
  return new Date(Date.UTC(from.getUTCFullYear() + 1, 0, 1, 0, 0, 0, 0));
}

/** How long ago, in its largest whole unit: "3s", "12m", "2h", "5d". */
export function formatCompactAge(timestamp: Date, now: number): string {
  const diffSec = Math.max(0, Math.floor((now - timestamp.getTime()) / 1000));
  if (diffSec < 60) return `${diffSec}s`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  return `${Math.floor(diffHr / 24)}d`;
}

// ── The platform's date-and-time format, via next-intl's `useFormatter` (UTC) ──
// "05 - APR - 2026 / 17:52:00", as Promo codes → History prints it.

/** Time of day, 24h — history rows. */
export const TIME_FMT = {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
  timeZone: 'UTC',
} as const;

const DATE_FMT_DAY = { day: '2-digit', timeZone: 'UTC' } as const;
const DATE_FMT_MONTH = { month: 'short', timeZone: 'UTC' } as const;
const DATE_FMT_YEAR = { year: 'numeric', timeZone: 'UTC' } as const;

/** Full date as "05 - APR - 2026" (deposit rows pass `-` for "05-APR-2026"). */
export function formatFullDate(
  format: ReturnType<typeof useFormatter>,
  at: string | Date,
  sep = ' - ',
): string {
  const d = new Date(at);
  const day = format.dateTime(d, DATE_FMT_DAY);
  const month = format.dateTime(d, DATE_FMT_MONTH).toUpperCase();
  const year = format.dateTime(d, DATE_FMT_YEAR);
  return [day, month, year].join(sep);
}

/** Hours and minutes, 24h — `h23`, so midnight is 00:05, never 24:05. */
const TIME_FMT_MINUTES = {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: 'UTC',
} as const;

/** A moment to the minute, zone named: "12-Aug-2026, 12:23 UTC". */
export function formatDateTimeUtc(
  format: ReturnType<typeof useFormatter>,
  at: string | Date,
): string {
  const d = new Date(at);
  const date = [
    format.dateTime(d, DATE_FMT_DAY),
    format.dateTime(d, DATE_FMT_MONTH),
    format.dateTime(d, DATE_FMT_YEAR),
  ].join('-');
  return `${date}, ${format.dateTime(d, TIME_FMT_MINUTES)} UTC`;
}

export function toUnixSeconds(iso: string): number {
  return Math.floor(new Date(iso).getTime() / 1000);
}
